import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Clock } from 'lucide-react';

const TX_STATUS_STYLE = {
  paid: 'bg-[#00E676]/20 text-[#00E676]',
  cancelled: 'bg-[#FF1744]/20 text-[#FF1744]',
};
const TX_STATUS_LABEL = { paid: 'Paid', cancelled: 'Cancelled' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(amount || 0);

/**
 * Merges wallet + subscription transactions into a single recent-activity
 * list (most recent 8). Pure presentational — receives both arrays from
 * the parent.
 */
export function RecentActivity({ transactions, subTransactions }) {
  const walletTxns = (transactions || []).slice(0, 5).map((tx) => ({
    key: `w-${tx.id || tx.created_at}`,
    description: tx.description || tx.type || 'Transaction',
    date: tx.created_at,
    amount: tx.amount,
    status: tx.status || 'completed',
    type: 'wallet',
  }));
  const subTxns = (subTransactions || []).slice(0, 5).map((tx) => ({
    key: `s-${tx.id || tx.created_at}`,
    description: `${tx.plan_name || 'Subscription'} Payment`,
    date: tx.created_at,
    amount: tx.amount,
    status: tx.display_status || tx.status,
    type: 'subscription',
  }));
  const allTxns = [...walletTxns, ...subTxns]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);

  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="font-outfit text-white">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {allTxns.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
            <p className="text-[#A0B4CC]">No recent transactions</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allTxns.map((tx) => (
              <div
                key={tx.key}
                className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]"
                data-testid={`activity-item-${tx.key}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{tx.description}</p>
                  <p className="text-[#5C7599] text-xs">{new Date(tx.date).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-3">
                  {tx.type === 'subscription' && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        TX_STATUS_STYLE[tx.status] || 'bg-[#FFEA00]/20 text-[#FFEA00]'
                      }`}
                      data-testid="sub-txn-status"
                    >
                      {TX_STATUS_LABEL[tx.status] || tx.status}
                    </span>
                  )}
                  <span
                    className={`font-mono text-sm flex-shrink-0 ${
                      tx.status === 'cancelled'
                        ? 'text-[#FF1744] line-through'
                        : tx.type === 'subscription' && tx.status === 'paid'
                        ? 'text-[#00E676]'
                        : tx.amount >= 0
                        ? 'text-[#00E676]'
                        : 'text-[#FF1744]'
                    }`}
                  >
                    {tx.type === 'subscription' ? '' : tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
